/**
 * Keyboard-navigable command menu components.
 * Based on https://ui.shadcn.com/docs/components/command
 *
 * @todo add lookbook documentation and examples
 */

import { ComponentProps, forwardRef } from "react";
import { useCommandState, Command as CommandPrimitive } from "cmdk";
import { twMerge } from "tailwind-merge";

function Command({ className, ...props }: ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={twMerge(
        "text-charcoal-700 flex h-full w-full flex-col gap-2 bg-white p-2",
        className,
      )}
      {...props}
    />
  );
}

const CommandInput = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof CommandPrimitive.Input> & {
    shortcut?: string;
  }
>(({ className, ...props }, ref) => {
  return (
    <div data-slot="command-input-wrapper">
      <CommandPrimitive.Input
        ref={ref}
        data-slot="command-input"
        className={twMerge(
          "input focus:border-lime-500 focus:ring-1 focus:ring-lime-500",
          className,
        )}
        {...props}
      />
    </div>
  );
});

function CommandList({ className, ...props }: ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={twMerge(
        "no-title-tooltip max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({ ...props }: ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="charcoal-300 py-6 text-center text-sm"
      {...props}
    />
  );
}

function CommandGroup({ className, ...props }: ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={twMerge(
        "[&_[cmdk-group-heading]]:charcoal-300 text-charcoal-700 overflow-hidden [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={twMerge("-mx-2 h-px bg-gray-500", className)}
      {...props}
    />
  );
}

function CommandItem({ className, ...props }: ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={twMerge(
        "[&_svg:not([class*='text-'])]:charcoal-300 outline-hidden relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm data-[disabled=true]:pointer-events-none data-[selected=true]:bg-purple-100 data-[selected=true]:text-purple-600 data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  useCommandState,
};
